import { ListChecks, ListOrdered } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@/components/ui/Button";
import {
  getResumptionHint,
  minutesToTimeString,
  type PlanOverlapConflict,
  validatePlanEndAgainstPlans,
} from "@/pages/main/utils/irrigationPlanOverlap";
import { formatDays } from "@/utils/format";
import ConfirmModal from "../../../../components/ConfirmModal";
import BulkActionBar from "./components/BulkActionBar";
import ChooseSectorsDialog from "./components/dialogs/ChooseSectorsDialog";
import StepEditDialog from "./components/dialogs/StepEditDialog";
import StepEditManyDialog from "./components/dialogs/StepEditManyDialog";
import StepReorderAssistant from "./components/dialogs/StepReorderAssistant";
import StepList from "./components/StepList";
import SummarySection from "./components/SummarySection";
import { useSectorManagement } from "./hooks/useSectorManagement";
import { useStepActions } from "./hooks/useStepActions";
import { useStepSelection } from "./hooks/useStepSelection";
import type { IrrigationPlanStepsPanel2Props } from "./types";

/**
 * IrrigationPlanStepsPanel2 - Modular implementation of irrigation plan steps management
 *
 * This component provides a card-based layout with bulk operations and sector selection dialog.
 * It has been refactored into a modular structure for better maintainability and reusability.
 */
function IrrigationPlanStepsPanel2({
  projectId,
  planData,
  steps,
  sectors,
  onAddStep,
  onRemoveStep,
  onSaveStep,
  onUpdateStep,
  onMoveStep,
  onBulkRemoveSteps,
  onReorderSteps,
  readOnly = false,
  loading = false,
  projectData,
  hasFrequencyInverter = false,
  planId,
  existingPlans,
  minimumGapMinutes,
  resumptionEnabled,
  withoutResumptionGap,
  mirrors,
}: IrrigationPlanStepsPanel2Props) {
  // Dialog state
  const [showSectorDialog, setShowSectorDialog] = useState(false);
  const [showReorderAssistant, setShowReorderAssistant] = useState(false);
  const [editingStep, setEditingStep] = useState<string | null>(null);
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    stepId: "",
    sectorName: "",
  });
  const [planOverlapError, setPlanOverlapError] = useState<string | null>(null);

  const normalizedPlanId = planId;

  const calculatePlanOverlap = useCallback(
    (candidateSteps: typeof steps) => {
      if (!planData.startTime || !planData.daysOfWeek || planData.daysOfWeek.length === 0) {
        return { isValid: true };
      }

      const totalDurationMinutes = candidateSteps.reduce((sum, step) => sum + (step.durationMinutes || 0), 0);

      if (totalDurationMinutes <= 0) {
        return { isValid: true };
      }

      return validatePlanEndAgainstPlans(
        {
          id: normalizedPlanId,
          name: planData.name,
          daysOfWeek: planData.daysOfWeek,
          startTime: planData.startTime,
          totalDurationMinutes,
        },
        existingPlans,
        minimumGapMinutes
      );
    },
    [existingPlans, minimumGapMinutes, normalizedPlanId, planData.daysOfWeek, planData.name, planData.startTime]
  );

  const currentOverlapResult = useMemo(() => calculatePlanOverlap(steps), [calculatePlanOverlap, steps]);

  const formatConflictMessage = useCallback((conflict: PlanOverlapConflict) => {
    const dayLabel = formatDays([conflict.day]);

    if (conflict.type === "end") {
      const latestAllowedEnd = minutesToTimeString(conflict.other.startMinutes - conflict.minimumGapMinutes);
      const nextPlanStart = minutesToTimeString(conflict.other.startMinutes);
      return `Conflito com o plano "${conflict.other.name}" em ${dayLabel}. Finalize até ${latestAllowedEnd} para manter ${conflict.minimumGapMinutes} minutos antes do início às ${nextPlanStart}.`;
    }

    const requiredStart = minutesToTimeString(conflict.other.endMinutes + conflict.minimumGapMinutes);
    return `Conflito com o plano "${conflict.other.name}" em ${dayLabel}. Inicie após ${requiredStart}.`;
  }, []);

  const planOverlapMessage = useMemo(() => {
    if (currentOverlapResult.isValid || !currentOverlapResult.conflict) {
      return null;
    }
    return formatConflictMessage(currentOverlapResult.conflict);
  }, [currentOverlapResult, formatConflictMessage]);

  useEffect(() => {
    if (planOverlapMessage) {
      setPlanOverlapError(null);
    }
  }, [planOverlapMessage]);

  const overlapMessageToDisplay = planOverlapMessage ?? planOverlapError;

  // Optional hint: disabling resumption would resolve the live conflict (only when it actually would).
  const resumptionHint = useMemo(() => {
    if (!planOverlapMessage || !currentOverlapResult.conflict) return null;
    return getResumptionHint(currentOverlapResult.conflict, { resumptionEnabled, withoutResumptionGap });
  }, [planOverlapMessage, currentOverlapResult.conflict, resumptionEnabled, withoutResumptionGap]);

  const validateStepAgainstOverlap = useCallback(
    (candidateStep: (typeof steps)[number]) => {
      const exists = steps.some((step) => step.id === candidateStep.id);
      const nextSteps = exists
        ? steps.map((step) => (step.id === candidateStep.id ? candidateStep : step))
        : [...steps, candidateStep];

      const result = calculatePlanOverlap(nextSteps);
      if (!result.isValid && result.conflict) {
        return formatConflictMessage(result.conflict);
      }
      return null;
    },
    [calculatePlanOverlap, formatConflictMessage, steps]
  );

  const validateBulkAgainstOverlap = useCallback(
    (updates: Partial<(typeof steps)[number]>, affectedStepIds: string[]) => {
      if (affectedStepIds.length === 0) {
        return null;
      }

      const nextSteps = steps.map((step) => (affectedStepIds.includes(step.id) ? { ...step, ...updates } : step));

      const result = calculatePlanOverlap(nextSteps);
      if (!result.isValid && result.conflict) {
        return formatConflictMessage(result.conflict);
      }
      return null;
    },
    [calculatePlanOverlap, formatConflictMessage, steps]
  );

  const saveStepWithValidation = useCallback(
    async (step: (typeof steps)[number]) => {
      const errorMessage = validateStepAgainstOverlap(step);
      if (errorMessage) {
        setPlanOverlapError(errorMessage);
        throw new Error(errorMessage);
      }

      setPlanOverlapError(null);
      await onSaveStep(step);
    },
    [onSaveStep, validateStepAgainstOverlap]
  );

  // Custom hooks for state management
  const { selectedSteps, toggleStepSelect, clearSelection, selectAll } = useStepSelection();

  const { selectedSectorIds, handleSectorsChange } = useSectorManagement({
    steps,
    sectors,
    planData,
    onRemoveStep,
    onUpdateStep,
    onSaveStep: saveStepWithValidation,
    onBulkRemoveSteps,
  });

  // Confirmation handlers
  const handleConfirmSingleDelete = (stepId: string, sectorName: string) => {
    setConfirmModal({
      isOpen: true,
      stepId,
      sectorName,
    });
  };

  const { handleStepEdit, handleStepDelete, handleStepSave, handleBulkEdit } = useStepActions({
    steps,
    onUpdateStep,
    onRemoveStep,
    onSaveStep: saveStepWithValidation,
    onConfirmSingleDelete: handleConfirmSingleDelete,
  });

  // Event handlers
  const handleEditStep = (stepId: string) => {
    setEditingStep(stepId);
  };

  const handleStepSaveAndClose = async (step: any) => {
    try {
      await handleStepSave(step);
      setEditingStep(null);
    } catch (error) {
      console.error("Erro ao salvar etapa:", error);
    }
  };

  const handleBulkEditClick = () => {
    setShowBulkEditDialog(true);
  };

  const handleBulkSave = async (updates: any) => {
    const stepIds = Array.from(selectedSteps);
    try {
      await handleBulkEdit(stepIds, updates);
      setShowBulkEditDialog(false);
      clearSelection();
    } catch (error) {
      console.error("Erro ao aplicar edição em massa:", error);
    }
  };

  // Handle step reordering
  const handleReorderSteps = (newOrder: any[]) => {
    if (onReorderSteps) {
      onReorderSteps(newOrder);
    }
    setShowReorderAssistant(false);
  };

  // Confirmation modal handlers
  const handleConfirmRemove = () => {
    onRemoveStep(confirmModal.stepId);
    setConfirmModal({
      isOpen: false,
      stepId: "",
      sectorName: "",
    });
  };

  const handleCancelRemove = () => {
    setConfirmModal({
      isOpen: false,
      stepId: "",
      sectorName: "",
    });
  };

  // Get the step being edited
  const editingStepData = editingStep ? steps.find((step) => step.id === editingStep) || null : null;

  // Compute mirror data for the StepEditDialog
  const mirrorDialogData = useMemo(() => {
    if (!editingStepData || !mirrors) {
      return null;
    }

    // Source sector IDs (sectors that have steps in this plan)
    const sourceSectorIds = new Set(steps.map((s) => s.sectorId));
    const sourceSectorNames = steps.map((s) => s.sectorName);

    // Existing mirrors for the current step
    const stepMirrors = mirrors.filter((m) => m.source_irrigation_plan_step === editingStepData.id);
    const existingMirrorSectorIds = stepMirrors.map((m) => m.target_sector.id);

    // Targets mirrored by other sources (not the current step)
    const otherSourceMirrorTargetIds = new Set(
      mirrors
        .filter((m) => m.source_irrigation_plan_step !== editingStepData.id)
        .map((m) => m.target_sector.id)
    );

    // Build mapping of mirrored targets → source sector name
    const mirroredTargetMap = new Map<string, string>();
    for (const mirror of mirrors) {
      const sourceStep = steps.find((s) => s.id === mirror.source_irrigation_plan_step);
      if (sourceStep) {
        mirroredTargetMap.set(mirror.target_sector.id, sourceStep.sectorName);
      }
    }

    // Already-mirrored targets (not for current step)
    const mirroredTargetInfo = mirrors
      .filter((m) => m.source_irrigation_plan_step !== editingStepData.id)
      .map((m) => {
        const sourceStep = steps.find((s) => s.id === m.source_irrigation_plan_step);
        return {
          targetSectorId: m.target_sector.id,
          targetSectorName: m.target_sector.name,
          sourceSectorName: sourceStep?.sectorName || "Desconhecido",
        };
      });

    // Available targets: non-source, not mirrored by another source
    // (current step's own mirrors are included so they appear checked)
    const mirrorTargetSectors = sectors.filter(
      (s) => !sourceSectorIds.has(s.id) && !otherSourceMirrorTargetIds.has(s.id)
    );

    return {
      mirrorTargetSectors,
      existingMirrorSectorIds,
      mirroredTargetInfo,
      sourceSectorNames,
    };
  }, [editingStepData, mirrors, steps, sectors]);

  // Build mirror names keyed by step ID (for step card indicators)
  const mirrorNamesByStepId = useMemo(() => {
    if (!mirrors) return undefined;
    const map: Record<string, string[]> = {};
    for (const mirror of mirrors) {
      const stepId = mirror.source_irrigation_plan_step;
      if (!map[stepId]) map[stepId] = [];
      map[stepId].push(mirror.target_sector.name);
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [mirrors]);

  // Build mirror target info for ChooseSectorsDialog (disabled sectors with indicator)
  const chooseDialogMirrorTargets = useMemo(() => {
    if (!mirrors) return undefined;
    const result: { sectorId: string; sourceSectorName: string }[] = [];
    const seen = new Set<string>();
    for (const mirror of mirrors) {
      const targetId = mirror.target_sector.id;
      if (seen.has(targetId)) continue;
      seen.add(targetId);
      const sourceStep = steps.find((s) => s.id === mirror.source_irrigation_plan_step);
      if (sourceStep) {
        result.push({ sectorId: targetId, sourceSectorName: sourceStep.sectorName });
      }
    }
    return result.length > 0 ? result : undefined;
  }, [mirrors, steps]);

  // Get selected steps for bulk editing
  const selectedStepsData = steps.filter((step) => selectedSteps.has(step.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Summary Section */}
      <SummarySection steps={steps} sectors={sectors} planData={planData} />

      {overlapMessageToDisplay && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">
          <p className="text-sm font-semibold">Conflito de agendamento</p>
          <p className="text-sm mt-1">{overlapMessageToDisplay}</p>
          {resumptionHint && <p className="text-xs italic mt-2 opacity-80">💡 {resumptionHint}</p>}
        </div>
      )}

      {/* Choose Sectors Button */}
      <div className="flex justify-center">
        <Button
          onClick={() => setShowSectorDialog(true)}
          disabled={readOnly}
          icon={<ListChecks />}
          variant="primary"
          fullWidth
        >
          Escolher Setores
        </Button>
      </div>

      {/* Step Reordering Assistant Button */}
      {!readOnly && steps.length > 1 && (
        <div className="flex justify-center">
          <Button onClick={() => setShowReorderAssistant(true)} icon={<ListOrdered />} variant="info" outline fullWidth>
            Assistente de Ordenação
          </Button>
        </div>
      )}

      {/* Step List */}
      <StepList
        steps={steps}
        selectedSteps={selectedSteps}
        fertigationEnabled={planData.fertigationEnabled}
        readOnly={readOnly}
        sectors={sectors}
        hasFrequencyInverter={hasFrequencyInverter}
        mirrorNamesByStepId={mirrorNamesByStepId}
        onToggleStepSelect={toggleStepSelect}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onMoveStep={onMoveStep}
        onEditStep={handleEditStep}
        onDeleteStep={handleStepDelete}
      />

      {/* Bulk Action Bar */}
      <BulkActionBar selectedCount={selectedSteps.size} readOnly={readOnly} onBulkEdit={handleBulkEditClick} />

      {/* Choose Sectors Dialog */}
      <ChooseSectorsDialog
        isOpen={showSectorDialog}
        onClose={() => setShowSectorDialog(false)}
        sectors={sectors}
        selectedSectorIds={selectedSectorIds}
        onSectorsChange={handleSectorsChange}
        mirrorTargetSectors={chooseDialogMirrorTargets}
      />

      {/* Step Edit Dialog */}
      <StepEditDialog
        isOpen={editingStep !== null}
        onClose={() => setEditingStep(null)}
        step={editingStepData}
        fertigationEnabled={planData.fertigationEnabled}
        projectId={projectId}
        projectData={projectData}
        onSave={handleStepSaveAndClose}
        validatePlanDuration={validateStepAgainstOverlap}
        {...(mirrorDialogData ? {
          mirrorTargetSectors: mirrorDialogData.mirrorTargetSectors,
          existingMirrorSectorIds: mirrorDialogData.existingMirrorSectorIds,
          mirroredTargetInfo: mirrorDialogData.mirroredTargetInfo,
          sourceSectorNames: mirrorDialogData.sourceSectorNames,
        } : {})}
      />

      {/* Step Edit Many Dialog */}
      <StepEditManyDialog
        isOpen={showBulkEditDialog}
        onClose={() => setShowBulkEditDialog(false)}
        selectedSteps={selectedStepsData}
        fertigationEnabled={planData.fertigationEnabled}
        projectData={projectData}
        onSave={handleBulkSave}
        validatePlanDuration={validateBulkAgainstOverlap}
      />

      {/* Step Reorder Assistant */}
      <StepReorderAssistant
        isOpen={showReorderAssistant}
        onClose={() => setShowReorderAssistant(false)}
        steps={steps}
        onReorderSteps={handleReorderSteps}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir o setor "${confirmModal.sectorName}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
  );
}

export default IrrigationPlanStepsPanel2;
